/** SUN-240 — New Purchase Order lines from the vendor's mapped products (server: GET /vendors/products/po-lines/:vendorId). */
import type { LineItem } from './PurchaseOrderEditorParts';

export type PoLineSuggestion = {
  productId: string; name: string; sku: string | null; hsnCode: string | null; gstRate: number;
  unit: string | null; rate: number | null; isPreferred: boolean;
  remainingShort: number; suggestedQty: number | null; note: string | null;
};
export type LineView = 'short' | 'all';
export type PoLinesResponse = { lines: PoLineSuggestion[]; shortCount: number; mappedCount: number; defaultView: LineView };

export const emptyItem = (): LineItem => ({ description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, productId: '', unit: null });

export function toLineItem(s: PoLineSuggestion): LineItem {
  return {
    description: s.name, hsnSac: s.hsnCode ?? '', quantity: s.suggestedQty, rate: s.rate ?? 0, gstRate: s.gstRate, discount: 0,
    productId: s.productId, unit: s.unit, source: 'vendor', preferred: s.isPreferred, note: s.note,
  };
}

/** "Only short" keeps the lines that still need buying (a qty, or a unit note asking the buyer for one). */
export function visibleLines(all: PoLineSuggestion[], view: LineView): PoLineSuggestion[] {
  return view === 'all' ? all : all.filter((s) => (s.suggestedQty ?? 0) > 0 || !!s.note);
}

/**
 * Replace the earlier vendor's untouched filled lines with `next`. Lines the buyer typed into, Browse / manual lines
 * and loaded PO lines stay; a next line for a product already kept is skipped (no duplicate rows). Never empty.
 */
export function mergeVendorLines(current: LineItem[], next: LineItem[]): LineItem[] {
  const kept = current.filter((i) => !(i.source === 'vendor' && !i.touched) && !!i.description);
  const have = new Set(kept.map((i) => i.productId).filter(Boolean));
  const out = [...kept, ...next.filter((i) => !i.productId || !have.has(i.productId))];
  return out.length ? out : [emptyItem()];
}

/** Units show as the stored codes (PCS, KGS, MTR, LTR); none → a dash. */
export function unitLabel(u: string | null | undefined): string {
  return (u ?? '').trim() || '—';
}

/** Only lines with a description and a qty above 0 go on the PO; a blank-qty suggestion adds nothing. */
export function linesToSave(items: LineItem[]) {
  return items.filter((i) => i.description && (i.quantity ?? 0) > 0).map((i) => ({
    description: i.description,
    hsnSac: i.hsnSac || undefined,
    quantity: i.quantity as number,
    rate: i.rate,
    gstRate: i.gstRate,
    discount: i.discount || 0,
    productId: i.productId || undefined,
  }));
}
